import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchCompanyProfile,
  saveCompanyProfile,
  suggestRegulations,
  CompanyProfilePayload,
  JurisdictionEntry,
  ContactEntry,
} from '../../../api/companyProfileApi';
import { Country, State, ICountry, IState } from 'country-state-city';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './CompanyProfilePage.module.css';
import { getMasterData, type MasterData } from '../../../api/masterDataApi';

const STEPS = ['Basic Info', 'Jurisdictions & Business', 'AI & Data', 'Contacts & Regulations'];

const INDUSTRY_OPTIONS = [
  'Technology', 'Finance & Banking', 'Healthcare', 'Education', 'Government',
  'Legal', 'Insurance', 'Manufacturing', 'Retail & E-commerce', 'Telecommunications',
  'Energy', 'Media & Entertainment', 'Real Estate', 'Transportation', 'Other',
];

const SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];

const BUSINESS_MODELS = ['B2B', 'B2C', 'B2G'];

const AI_USAGE_OPTIONS = [
  { value: 'build', label: 'Build AI systems' },
  { value: 'deploy', label: 'Deploy AI systems' },
  { value: 'use', label: 'Use AI tools/services' },
];

const ALL_REGULATIONS = [
  'GDPR', 'EU AI Act', 'CCPA', 'India DPDPA', 'ISO 27001', 'ISO 27701',
  'SOC 2', 'UK GDPR', 'UK Data Protection Act 2018', 'LGPD', 'PIPEDA',
  'PIPL', 'PDPA', 'APPI', 'PIPA', 'China AI Regulations', 'Privacy Act 1988',
];

const HIGH_COMPLEXITY: Record<string, string> = {
  'EU': 'EU has GDPR + EU AI Act — strictest global data & AI regulation framework.',
  'California': 'California has CCPA/CPRA — most extensive US state privacy law.',
  'India': 'India DPDPA 2023 — comprehensive data protection law with significant penalties.',
};

const CONTACT_ROLES = ['DPO', 'CAIO', 'Legal Owner', 'Compliance Owner'];

type FormState = {
  company_name: string;
  industry: string;
  company_size: string;
  headquarters_location: string;
  jurisdictions: JurisdictionEntry[];
  business_model: string[];
  ai_usage: string[];
  processes_personal_data: boolean | null;
  regulatory_declarations: string[];
  contacts: ContactEntry[];
};

const blankContact = (): ContactEntry => ({ role: '', name: '', email: '', title: '' });

const initial: FormState = {
  company_name: '',
  industry: '',
  company_size: '',
  headquarters_location: '',
  jurisdictions: [],
  business_model: [],
  ai_usage: [],
  processes_personal_data: null,
  regulatory_declarations: [],
  contacts: [blankContact()],
};

export default function CompanyProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Industry / size lists from master data (SRS #87); constants are the fallback.
  const [md, setMd] = useState<MasterData | null>(null);
  useEffect(() => { getMasterData().then(setMd).catch(() => {}); }, []);
  const industryOptions = md?.industries ?? INDUSTRY_OPTIONS;
  const sizeOptions = md?.company_sizes ?? SIZE_OPTIONS;
  const isEnterpriseFlow = location.pathname.startsWith('/enterprise/dashboard');
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Set when the profile GET failed for a reason OTHER than 404. While true we
  // must not let the user save, because the form is empty by accident and a save
  // would overwrite their stored profile with blanks.
  const [loadError, setLoadError] = useState(false);
  const [suggestedRegs, setSuggestedRegs] = useState<string[]>([]);
  const [showSummary, setShowSummary] = useState(false);

  // Jurisdiction picker state
  const [selCountry, setSelCountry] = useState('');
  const [selState, setSelState] = useState('');

  const countries: ICountry[] = Country.getAllCountries();

  useEffect(() => {
    fetchCompanyProfile()
      .then(p => {
        setForm({
          company_name: p.company_name || '',
          industry: p.industry || '',
          company_size: p.company_size || '',
          headquarters_location: p.headquarters_location || '',
          jurisdictions: p.operating_jurisdictions || [],
          business_model: p.business_model || [],
          ai_usage: p.ai_usage_declaration || [],
          processes_personal_data: p.processes_personal_data,
          regulatory_declarations: p.regulatory_declarations || [],
          contacts: p.contacts.length ? p.contacts : [blankContact()],
        });
      })
      .catch((err) => {
        // A 404 genuinely means "no profile yet" — render the blank wizard.
        // ANY other error must NOT be treated that way: the form then shows
        // empty, and saving it wipes the real profile server-side (contacts:[]
        // deletes every DPO/compliance contact, company_name:'' blanks the name).
        const status = (err as { status?: number } | null)?.status;
        if (status && status !== 404) setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  // Auto-suggest regulations when jurisdictions change
  useEffect(() => {
    if (form.jurisdictions.length === 0) {
      setSuggestedRegs([]);
      return;
    }
    suggestRegulations(form.jurisdictions)
      .then(setSuggestedRegs)
      .catch(() => {});
  }, [form.jurisdictions]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleChip = (key: 'business_model' | 'ai_usage' | 'regulatory_declarations', value: string) => {
    setForm(prev => {
      const arr = prev[key] as string[];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const addJurisdiction = () => {
    if (!selCountry) return;
    const country = countries.find(c => c.isoCode === selCountry);
    if (!country) return;
    const entry: JurisdictionEntry = { country: country.name, state: selState || undefined };
    const exists = form.jurisdictions.some(j => j.country === entry.country && j.state === entry.state);
    if (!exists) set('jurisdictions', [...form.jurisdictions, entry]);
    setSelCountry('');
    setSelState('');
  };

  const removeJurisdiction = (idx: number) => {
    set('jurisdictions', form.jurisdictions.filter((_, i) => i !== idx));
  };

  const getHighComplexityKey = (j: JurisdictionEntry): string | null => {
    // Check EU membership
    const euCountries = ['Austria','Belgium','Bulgaria','Croatia','Cyprus','Czech Republic','Denmark','Estonia','Finland','France','Germany','Greece','Hungary','Ireland','Italy','Latvia','Lithuania','Luxembourg','Malta','Netherlands','Poland','Portugal','Romania','Slovakia','Slovenia','Spain','Sweden'];
    if (euCountries.includes(j.country)) return 'EU';
    if (j.state === 'California' || j.country === 'California') return 'California';
    if (j.country === 'India') return 'India';
    return null;
  };

  const updateContact = (idx: number, field: keyof ContactEntry, value: string) => {
    const updated = [...form.contacts];
    (updated[idx] as any)[field] = value;
    set('contacts', updated);
  };

  const addContact = () => set('contacts', [...form.contacts, blankContact()]);
  const removeContact = (idx: number) => set('contacts', form.contacts.filter((_, i) => i !== idx));

  const canNext = () => {
    if (step === 0) return !!form.company_name.trim();
    return true;
  };

  const handleSaveDraft = async () => {
    setError(null);
    // Refuse to save while the profile failed to load: the form is blank by
    // accident, and saving would overwrite the stored profile with empty values
    // (deleting all compliance contacts and blanking the company name).
    if (loadError) {
      setError('Could not load your saved profile, so saving is disabled to avoid overwriting it. Please reload the page.');
      return;
    }
    try {
      await saveCompanyProfile(buildPayload('incomplete'));
    } catch (err: any) {
      setError(err.message || 'Failed to save draft');
    }
  };

  const buildPayload = (status: string): CompanyProfilePayload => ({
    company_name: form.company_name,
    industry: form.industry || undefined,
    company_size: form.company_size || undefined,
    headquarters_location: form.headquarters_location || undefined,
    operating_jurisdictions: form.jurisdictions.length ? form.jurisdictions : undefined,
    business_model: form.business_model.length ? form.business_model : undefined,
    ai_usage_declaration: form.ai_usage.length ? form.ai_usage : undefined,
    processes_personal_data: form.processes_personal_data ?? undefined,
    regulatory_declarations: form.regulatory_declarations.length ? form.regulatory_declarations : undefined,
    contacts: form.contacts.filter(c => c.name.trim() && c.email.trim()).map(c => ({
      role: c.role, name: c.name, email: c.email, title: c.title || undefined,
    })),
    completion_status: status,
  });

  const handleSubmit = async () => {
    if (!showSummary) {
      setShowSummary(true);
      return;
    }
    setError(null);
    // Refuse to save while the profile failed to load: the form is blank by
    // accident, and saving would overwrite the stored profile with empty values
    // (deleting all compliance contacts and blanking the company name).
    if (loadError) {
      setError('Could not load your saved profile, so saving is disabled to avoid overwriting it. Please reload the page.');
      return;
    }
    setSubmitting(true);
    try {
      await saveCompanyProfile(buildPayload('complete'));
      navigate(isEnterpriseFlow ? '/enterprise/dashboard/repository' : '/grc/repository');
    } catch (err: any) {
      setError(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const states: IState[] = selCountry ? State.getStatesOfCountry(selCountry) : [];

  if (loading) return <div className={styles.loadingPage}>Loading...</div>;

  if (showSummary) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.header}>
            <Eyebrow tone="default" dot>REVIEW &amp; SUBMIT</Eyebrow>
            <PageHeading
              accentWord="Profile"
              accentTone="orange"
              size="h2"
              align="left"
              level="h1"
              onDark
            >
              Review Company Profile
            </PageHeading>
            <p>Please review your information before submitting.</p>
          </div>
          <div className={styles.body}>
            <div className={styles.summary}>
              <div className={styles.summarySection}>
                <h3>Basic Information</h3>
                <p><strong>Company:</strong> {form.company_name}</p>
                <p><strong>Industry:</strong> {form.industry || '-'}</p>
                <p><strong>Size:</strong> {form.company_size || '-'}</p>
                <p><strong>HQ:</strong> {form.headquarters_location || '-'}</p>
              </div>
              <div className={styles.summarySection}>
                <h3>Jurisdictions & Business Model</h3>
                <p><strong>Jurisdictions:</strong> {form.jurisdictions.map(j => `${j.country}${j.state ? ` - ${j.state}` : ''}`).join(', ') || '-'}</p>
                <p><strong>Business Model:</strong> {form.business_model.join(', ') || '-'}</p>
              </div>
              <div className={styles.summarySection}>
                <h3>AI & Data</h3>
                <p><strong>AI Usage:</strong> {form.ai_usage.join(', ') || '-'}</p>
                <p><strong>Processes Personal Data:</strong> {form.processes_personal_data === null ? '-' : form.processes_personal_data ? 'Yes' : 'No'}</p>
              </div>
              <div className={styles.summarySection}>
                <h3>Contacts</h3>
                {form.contacts.filter(c => c.name.trim()).map((c, i) => (
                  <p key={i}><strong>{c.role || 'Contact'}:</strong> {c.name} ({c.email})</p>
                ))}
                {!form.contacts.some(c => c.name.trim()) && <p>-</p>}
              </div>
              <div className={styles.summarySection}>
                <h3>Regulatory Declarations</h3>
                <p>{form.regulatory_declarations.join(', ') || '-'}</p>
              </div>
            </div>
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <button className={styles.backBtn} onClick={() => setShowSummary(false)}>Edit</button>
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Confirm & Submit'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <Eyebrow tone="default" dot>GRC</Eyebrow>
          <PageHeading
            accentWord="Profile"
            accentTone="orange"
            size="h2"
            align="left"
            level="h1"
            onDark
          >
            Company Profile
          </PageHeading>
          <p>Complete your company profile to personalise your GRC assessment.</p>
        </div>

        {/* Step indicator */}
        <div className={styles.steps}>
          {STEPS.map((s, i) => (
            <div key={i} className={`${styles.stepItem} ${i === step ? styles.active : ''} ${i < step ? styles.done : ''}`}>
              <div className={styles.stepDot}>{i < step ? '\u2713' : i + 1}</div>
              <span className={styles.stepLabel}>{s}</span>
            </div>
          ))}
        </div>

        <div className={styles.body}>

          {/* Step 0: Basic Info */}
          {step === 0 && (
            <>
              <div className={styles.field}>
                <label>Company Name <span className={styles.req}>*</span></label>
                <input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Acme Corp" />
              </div>
              <div className={styles.field}>
                <label>Industry</label>
                <select value={form.industry} onChange={e => set('industry', e.target.value)}>
                  <option value="">Select industry...</option>
                  {industryOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <label>Company Size</label>
                <select value={form.company_size} onChange={e => set('company_size', e.target.value)}>
                  <option value="">Select size...</option>
                  {sizeOptions.map(o => <option key={o} value={o}>{o} employees</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <label>Headquarters Location</label>
                <input value={form.headquarters_location} onChange={e => set('headquarters_location', e.target.value)} placeholder="e.g. San Francisco, CA, USA" />
              </div>
            </>
          )}

          {/* Step 1: Jurisdictions & Business Model */}
          {step === 1 && (
            <>
              <div className={styles.field}>
                <label>Operating Jurisdictions</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select style={{ flex: 1 }} value={selCountry} onChange={e => { setSelCountry(e.target.value); setSelState(''); }}>
                    <option value="">Select country...</option>
                    {countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                  </select>
                  {states.length > 0 && (
                    <select style={{ flex: 1 }} value={selState} onChange={e => setSelState(e.target.value)}>
                      <option value="">All / Select state...</option>
                      {states.map(s => <option key={s.isoCode} value={s.name}>{s.name}</option>)}
                    </select>
                  )}
                  <button type="button" className={styles.nextBtn} style={{ padding: '8px 16px', fontSize: 13 }} onClick={addJurisdiction} disabled={!selCountry}>Add</button>
                </div>
              </div>
              {form.jurisdictions.length > 0 && (
                <div className={styles.jurisdictionList}>
                  {form.jurisdictions.map((j, i) => {
                    const hcKey = getHighComplexityKey(j);
                    return (
                      <div key={i} className={styles.jurisdictionTag}>
                        <span>
                          {j.country}{j.state ? ` - ${j.state}` : ''}
                          {hcKey && (
                            <span className={styles.highComplexity} style={{ marginLeft: 6 }}>
                              &#9888;
                              <span className={styles.tooltip}>{HIGH_COMPLEXITY[hcKey]}</span>
                            </span>
                          )}
                        </span>
                        <button onClick={() => removeJurisdiction(i)}>&times;</button>
                      </div>
                    );
                  })}
                </div>
              )}
              {suggestedRegs.length > 0 && (
                <div className={styles.suggestions}>
                  <strong>Suggested regulations:</strong> {suggestedRegs.join(', ')}
                </div>
              )}
              <div className={styles.field}>
                <label>Business Model</label>
                <div className={styles.chipGroup}>
                  {BUSINESS_MODELS.map(bm => (
                    <button key={bm} type="button" className={`${styles.chip} ${form.business_model.includes(bm) ? styles.selected : ''}`} onClick={() => toggleChip('business_model', bm)}>
                      {bm}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Step 2: AI & Data */}
          {step === 2 && (
            <>
              <div className={styles.field}>
                <label>AI Usage Declaration</label>
                <div className={styles.chipGroup}>
                  {AI_USAGE_OPTIONS.map(opt => (
                    <button key={opt.value} type="button" className={`${styles.chip} ${form.ai_usage.includes(opt.value) ? styles.selected : ''}`} onClick={() => toggleChip('ai_usage', opt.value)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.field}>
                <label>Do you process personal data?</label>
                <div className={styles.radioGroup}>
                  <button type="button" className={`${styles.radioBtn} ${form.processes_personal_data === true ? styles.selected : ''}`} onClick={() => set('processes_personal_data', true)}>Yes</button>
                  <button type="button" className={`${styles.radioBtn} ${form.processes_personal_data === false ? styles.selected : ''}`} onClick={() => set('processes_personal_data', false)}>No</button>
                </div>
              </div>
            </>
          )}

          {/* Step 3: Contacts & Regulations */}
          {step === 3 && (
            <>
              <div className={styles.field}>
                <label>Key Contacts</label>
                {form.contacts.map((contact, i) => (
                  <div key={i} className={styles.contactCard}>
                    <div className={styles.row}>
                      <select value={contact.role} onChange={e => updateContact(i, 'role', e.target.value)} style={{ flex: 0.8 }}>
                        <option value="">Role...</option>
                        {CONTACT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <input value={contact.name} onChange={e => updateContact(i, 'name', e.target.value)} placeholder="Full name" style={{ flex: 1 }} />
                    </div>
                    <div className={styles.row}>
                      <input value={contact.email} onChange={e => updateContact(i, 'email', e.target.value)} placeholder="Email" type="email" style={{ flex: 1 }} />
                      <input value={contact.title || ''} onChange={e => updateContact(i, 'title', e.target.value)} placeholder="Job title (optional)" style={{ flex: 1 }} />
                    </div>
                    {form.contacts.length > 1 && (
                      <button className={styles.contactCard + ' ' + styles.removeBtn} onClick={() => removeContact(i)} style={{ background: 'none', border: 'none', color: 'var(--ds-danger-text)', cursor: 'pointer', fontSize: 13, alignSelf: 'flex-end', padding: 0 }}>Remove</button>
                    )}
                  </div>
                ))}
                <button type="button" className={styles.addContactBtn} onClick={addContact}>+ Add another contact</button>
              </div>

              <div className={styles.field}>
                <label>Regulatory Declarations</label>
                <div className={styles.chipGroup}>
                  {ALL_REGULATIONS.map(reg => (
                    <button
                      key={reg}
                      type="button"
                      className={`${styles.chip} ${form.regulatory_declarations.includes(reg) ? styles.selected : ''}`}
                      onClick={() => toggleChip('regulatory_declarations', reg)}
                      style={suggestedRegs.includes(reg) && !form.regulatory_declarations.includes(reg) ? { borderColor: 'var(--ds-brand-blue)', background: 'var(--ds-info-bg)' } : {}}
                    >
                      {reg}
                      {suggestedRegs.includes(reg) && !form.regulatory_declarations.includes(reg) && (
                        <span style={{ fontSize: 10, marginLeft: 4, color: 'var(--ds-link)' }}>suggested</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {loadError && (
          <div style={{
            margin: '0 0 14px', padding: '12px 16px', borderRadius: 10,
            background: 'rgba(240,68,56,0.08)', border: '1px solid rgba(240,68,56,0.4)',
            color: 'var(--ds-text)', fontSize: 14, lineHeight: 1.5,
          }}>
            <strong>Could not load your saved company profile.</strong>
            <div style={{ marginTop: 4 }}>
              The form below is blank because of that error — it is <em>not</em> your saved data.
              Saving is disabled so your stored profile and compliance contacts are not
              overwritten. Please reload the page to try again.
            </div>
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button className={styles.saveDraft} onClick={handleSaveDraft} disabled={loadError}>Save Draft</button>
          {step > 0 && (
            <button className={styles.backBtn} onClick={() => setStep(s => s - 1)}>Back</button>
          )}
          {step < STEPS.length - 1 ? (
            <button className={styles.nextBtn} onClick={() => setStep(s => s + 1)} disabled={!canNext()}>Next</button>
          ) : (
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
              Review & Submit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
